import {
  CONTENT_GATE_MESSAGES,
  CONTENT_STATUS_LABELS,
  CONTENT_STATUSES,
  PILLAR_LABELS,
  PILLARS,
  canTransition,
  contentGateFor,
  type ContentItemDto,
  type ContentStatus,
} from '@nova/shared';
import {
  Activity,
  CalendarDays,
  Columns3,
  GanttChartSquare,
  List,
  Plus,
  Radar,
  Search,
  X,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useFilterParams } from '../app/FilterProvider.js';
import { Button } from '../components/Button.js';
import { EmptyState, ErrorState } from '../components/EmptyState.js';
import { GlassPanel } from '../components/GlassPanel.js';
import { SkeletonPanel } from '../components/Skeleton.js';
import { CalendarView } from '../features/content/CalendarView.js';
import { ContentDrawer } from '../features/content/ContentDrawer.js';
import { ContentRow } from '../features/content/ContentRow.js';
import { KanbanBoard } from '../features/content/KanbanBoard.js';
import { FlowExplainer } from '../features/content/FlowExplainer.js';
import { useNewContent } from '../features/content/NewContentProvider.js';
import { RhythmView } from '../features/content/RhythmView.js';
import { SignalsPanel } from '../features/signals/SignalsPanel.js';
import { TimelineView } from '../features/content/TimelineView.js';
import type { Grouping } from '../features/content/timeline.js';
import { useBoardMove, useContentList, useReorderContent } from '../features/content/useContent.js';
import { cn } from '../lib/cn.js';

type View = 'timeline' | 'board' | 'calendar' | 'list' | 'rhythm';

const VIEWS: { key: View; label: string; Icon: typeof List }[] = [
  { key: 'timeline', label: 'Tidslinje', Icon: GanttChartSquare },
  { key: 'board', label: 'Tavle', Icon: Columns3 },
  { key: 'calendar', label: 'Kalender', Icon: CalendarDays },
  { key: 'list', label: 'Liste', Icon: List },
  { key: 'rhythm', label: 'Rytme', Icon: Activity },
];

const GROUPINGS: { key: Grouping; label: string }[] = [
  // Default: what has to happen soonest, across everything. Narrowing to one
  // product area is what the filters above are for.
  { key: 'next', label: 'Neste aktivitet' },
  { key: 'area', label: 'Område' },
  { key: 'owner', label: 'Ansvarlig' },
  { key: 'pillar', label: 'Pilar' },
];

// 0 shows the plan from today on — history off (07 — 2.1.9).
const LOOKBACKS = [0, 30, 60, 90];

/** Pixels per day — how far the timeline is zoomed in. */
const ZOOMS = [
  { label: 'Tett', value: 4 },
  { label: 'Normal', value: 8 },
  { label: 'Vid', value: 16 },
];

export function EditorialPlanPage() {
  const { contentId } = useParams();
  const navigate = useNavigate();
  const globalFilter = useFilterParams();
  const { open: openNewContent } = useNewContent();

  // A tile on the dashboard links here with ?status=… or ?overdue=1 — open on the
  // list view already narrowed to those cases.
  const [searchParams] = useSearchParams();
  const paramStatus = searchParams.get('status');
  const initialStatus =
    paramStatus && (CONTENT_STATUSES as readonly string[]).includes(paramStatus)
      ? (paramStatus as ContentStatus)
      : undefined;
  const initialOverdue = searchParams.get('overdue') === '1';

  const [view, setView] = useState<View>(initialStatus || initialOverdue ? 'list' : 'board');
  const [showSignals, setShowSignals] = useState(false);
  const [grouping, setGrouping] = useState<Grouping>('next');
  const [lookbackDays, setLookbackDays] = useState(60);
  const [dayWidth, setDayWidth] = useState(8);
  const [month, setMonth] = useState(() => new Date());
  const [search, setSearch] = useState('');
  const [pillar, setPillar] = useState<string | undefined>();
  const [status, setStatus] = useState<ContentStatus | undefined>(initialStatus);
  const [overdueOnly, setOverdueOnly] = useState(initialOverdue);
  const [openId, setOpenId] = useState<string | null>(contentId ?? null);
  // Set when a blocked board move opens the card to explain what is missing —
  // the drawer shows it and puts the cursor in the content field.
  const [gateNotice, setGateNotice] = useState<string | null>(null);
  // The card the editor just closed on — flashed briefly on the board so it is
  // easy to spot which one you were working on. Clears itself after ~1.5s.
  const [flashId, setFlashId] = useState<string | null>(null);
  const flashTimer = useRef<number | null>(null);

  useEffect(() => setOpenId(contentId ?? null), [contentId]);

  // Never leave a pending flash timer behind when the page unmounts.
  useEffect(() => () => window.clearTimeout(flashTimer.current ?? undefined), []);

  const { data, isLoading, isError, refetch } = useContentList({
    ...globalFilter,
    pillar,
    status,
    search: search.trim() || undefined,
    overdueOnly: overdueOnly || undefined,
    // Keep avvist/kansellert/utgått out of the active views unless the user picks
    // one of those statuses explicitly (they live in the board's «Utgått» column).
    activeOnly: status ? undefined : true,
  });

  const items = data?.items ?? [];
  const hasFilters = Boolean(pillar || status || search.trim() || overdueOnly);

  function openItem(item: ContentItemDto, notice?: string) {
    setOpenId(item.id);
    setGateNotice(notice ?? null);
    navigate(`/plan/${item.id}`, { replace: true });
  }

  function closeItem() {
    const wasOpen = openId;
    setOpenId(null);
    setGateNotice(null);
    navigate('/plan', { replace: true });
    // Point out the card we just left, so it is easy to find again on the board.
    if (wasOpen) {
      setFlashId(wasOpen);
      window.clearTimeout(flashTimer.current ?? undefined);
      flashTimer.current = window.setTimeout(() => setFlashId(null), 1500);
    }
  }

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-ink text-xl">Redaksjonsplan</h1>
          <p className="text-ink-soft mt-0.5 text-sm">
            {isLoading
              ? 'Henter planen …'
              : `${data?.total ?? 0} saker${hasFilters ? ' med gjeldende filtre' : ''}`}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <div className="border-hairline bg-panel/60 flex rounded-lg border p-0.5">
            {VIEWS.map(({ key, label, Icon }) => (
              <button
                key={key}
                type="button"
                // The label is hidden below `sm`, so the button needs a name of
                // its own to stay reachable for screen readers on mobile.
                aria-label={label}
                aria-pressed={view === key}
                onClick={() => setView(key)}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs transition',
                  view === key ? 'bg-accent/15 text-accent' : 'text-ink-soft hover:text-ink',
                )}
              >
                <Icon size={13} aria-hidden />
                <span className="hidden sm:inline">{label}</span>
              </button>
            ))}
          </div>

          <Button
            variant={showSignals ? 'primary' : 'secondary'}
            size="sm"
            aria-pressed={showSignals}
            icon={<Radar size={14} aria-hidden />}
            onClick={() => setShowSignals((v) => !v)}
          >
            <span className="hidden sm:inline">Signaler</span>
          </Button>

          <Button
            variant="primary"
            size="sm"
            icon={<Plus size={14} aria-hidden />}
            onClick={openNewContent}
          >
            <span className="hidden sm:inline">Nytt innlegg</span>
          </Button>
        </div>
      </header>

      {showSignals && (
        <GlassPanel>
          <div className="mb-3 flex items-center justify-between">
            <div>
              <h2 className="font-display text-ink text-base">Signaler fra omverdenen</h2>
              <p className="text-ink-soft mt-0.5 text-[11px]">
                Nyheter og hendelser Nova har fanget opp. «Lag forslag» lager et forslagskort du kan
                bearbeide.
              </p>
            </div>
          </div>
          <SignalsPanel take={12} />
        </GlassPanel>
      )}

      {/* Filter chips */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search
            size={14}
            className="text-ink-soft pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2"
            aria-hidden
          />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Søk i titler og briefer"
            aria-label="Søk i redaksjonsplanen"
            className="border-hairline bg-panel/60 text-ink placeholder:text-ink-soft/60 h-9 w-56 rounded-lg border pl-8 pr-3 text-xs"
          />
        </div>

        <FilterSelect
          label="Pilar"
          value={pillar ?? ''}
          onChange={(v) => setPillar(v || undefined)}
          options={PILLARS.map((p) => ({ value: p, label: PILLAR_LABELS[p] }))}
        />

        <FilterSelect
          label="Status"
          value={status ?? ''}
          onChange={(v) => setStatus((v || undefined) as ContentStatus | undefined)}
          options={CONTENT_STATUSES.map((s) => ({ value: s, label: CONTENT_STATUS_LABELS[s] }))}
        />

        <button
          type="button"
          onClick={() => setOverdueOnly((v) => !v)}
          className={cn(
            'h-9 rounded-lg border px-3 text-xs transition',
            overdueOnly
              ? 'border-danger/40 bg-danger/12 text-danger'
              : 'border-hairline bg-panel/60 text-ink-soft hover:text-ink',
          )}
        >
          Kun forsinket
        </button>

        {hasFilters && (
          <button
            type="button"
            onClick={() => {
              setPillar(undefined);
              setStatus(undefined);
              setSearch('');
              setOverdueOnly(false);
            }}
            className="text-ink-soft hover:text-ink inline-flex h-9 items-center gap-1 px-2 text-xs"
          >
            <X size={13} aria-hidden />
            Nullstill
          </button>
        )}

        <div className="ml-auto">
          <FlowExplainer />
        </div>
      </div>

      {view === 'timeline' && (
        <div className="flex flex-wrap items-center gap-2">
          <SegmentedControl
            label="Sorter"
            options={GROUPINGS.map((g) => ({ value: g.key, label: g.label }))}
            value={grouping}
            onChange={(value) => setGrouping(value as Grouping)}
          />
          <SegmentedControl
            label="Historikk"
            options={LOOKBACKS.map((days) => ({ value: String(days), label: `${days} d` }))}
            value={String(lookbackDays)}
            onChange={(value) => setLookbackDays(Number(value))}
          />
          <SegmentedControl
            label="Zoom"
            options={ZOOMS.map((z) => ({ value: String(z.value), label: z.label }))}
            value={String(dayWidth)}
            onChange={(value) => setDayWidth(Number(value))}
          />
        </div>
      )}

      {/* The rhythm view has its own data — an empty plan is exactly what it
          is there to expose, so it never hides behind the empty state. */}
      {view === 'rhythm' ? (
        <RhythmView />
      ) : isError ? (
        <GlassPanel>
          <ErrorState onRetry={() => void refetch()} />
        </GlassPanel>
      ) : isLoading ? (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <SkeletonPanel key={i} lines={2} />
          ))}
        </div>
      ) : items.length === 0 ? (
        <GlassPanel>
          <EmptyState
            title={hasFilters ? 'Ingen treff med disse filtrene.' : 'Planen er tom.'}
            description={
              hasFilters
                ? 'Prøv å fjerne et filter, eller søk på noe annet.'
                : 'Nova legger inn forslag når Redaksjonsplanleggeren kjører — eller legg inn noe selv.'
            }
            action={
              !hasFilters && (
                <Button variant="primary" size="sm" onClick={openNewContent}>
                  Legg inn innhold
                </Button>
              )
            }
          />
        </GlassPanel>
      ) : view === 'timeline' ? (
        <GlassPanel padded={false} className="p-3">
          <TimelineView
            items={items}
            grouping={grouping}
            lookbackDays={lookbackDays}
            dayWidth={dayWidth}
            onOpen={openItem}
          />
        </GlassPanel>
      ) : view === 'board' ? (
        <BoardView items={items} onOpen={openItem} flashId={flashId} />
      ) : view === 'calendar' ? (
        <GlassPanel>
          <CalendarView items={items} month={month} onMonthChange={setMonth} onOpen={openItem} />
        </GlassPanel>
      ) : (
        <GlassPanel padded={false}>
          <div className="space-y-1 p-3">
            {items.map((item) => (
              <ContentRow key={item.id} item={item} onOpen={openItem} />
            ))}
          </div>
        </GlassPanel>
      )}

      <ContentDrawer contentId={openId} onClose={closeItem} notice={gateNotice} />
    </div>
  );
}

/**
 * Drag-and-drop on the board performs the transitions that need no extra input.
 * Everything else (reject, schedule) opens the drawer, where the required
 * fields are collected.
 */
function BoardView({
  items,
  onOpen,
  flashId,
}: {
  items: ContentItemDto[];
  onOpen: (item: ContentItemDto, notice?: string) => void;
  /** A card to briefly highlight — set right after its editor closes. */
  flashId: string | null;
}) {
  const boardMove = useBoardMove();
  const reorder = useReorderContent();
  const filter = useFilterParams();

  function move(item: ContentItemDto, to: ContentStatus) {
    if (!canTransition(item.status, to)) return;

    // A move the machine allows but that has no draft yet: open the card and
    // explain, with the cursor in the content field, instead of failing.
    const gate = contentGateFor(to, {
      hasContent: Boolean(item.body?.trim()),
      draftRequested: Boolean(item.draftRequestedAt),
    });
    if (gate) {
      onOpen(item, CONTENT_GATE_MESSAGES[gate]);
      return;
    }

    // The one-click moves that need no extra input.
    if (to === 'APPROVED') return void boardMove.mutate({ id: item.id, action: 'approve' });
    if (to === 'DRAFT') return void boardMove.mutate({ id: item.id, action: 'to-draft' });
    if (to === 'SUGGESTED') return void boardMove.mutate({ id: item.id, action: 'reopen' });

    // Scheduling, publishing, rejecting and cancelling need a decision.
    onOpen(item);
  }

  return (
    <KanbanBoard
      items={items}
      onOpen={onOpen}
      onMove={move}
      onReorder={(status, orderedIds) => reorder.mutate({ status, orderedIds })}
      filter={{ company: filter.company, areaId: filter.areaId }}
      flashId={flashId}
    />
  );
}

function SegmentedControl({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: { value: string; label: string }[];
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="border-hairline bg-panel/60 flex items-center gap-0.5 rounded-lg border p-0.5">
      <span className="label-micro px-2">{label}</span>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          aria-pressed={value === option.value}
          onClick={() => onChange(option.value)}
          className={cn(
            'rounded-md px-2.5 py-1 text-xs transition',
            value === option.value ? 'bg-accent/15 text-accent' : 'text-ink-soft hover:text-ink',
          )}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

function FilterSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      aria-label={label}
      className={cn(
        'h-9 rounded-lg border px-2.5 text-xs transition',
        value
          ? 'border-accent/40 bg-accent/10 text-accent'
          : 'border-hairline bg-panel/60 text-ink-soft',
      )}
    >
      <option value="">{label}: alle</option>
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
}
